'use client';

/**
 * The pieces every data pane draws around a react-query read, authored ONCE -- the failure reader, the failure alert,
 * and the placeholder silhouettes. Sibling of `NoticeAlert` in the same directory and hoisted for the same reason it
 * was: the panes each carried their own copy, and one of them carried the failure reader three times over -- a named
 * helper, a differently-named twin, and a third spelling inlined into a hook.
 *
 * `readQueryError` IS THE CANONICAL NAME, and it is not a preference: `QUAL-FRONTEND-QUERY-ERROR-BLIND-001`'s `fix`
 * names this shape -- hand the WHOLE query object on, so its failure is read somewhere -- and its escape analysis is
 * what keeps that rule false-positive-free. Passing `query.error` alone instead would read as a hook whose failure
 * signal nobody consults.
 *
 * ROWS ARE OUTLINED, NOT ELEVATED, on every surface that draws these. A page of stacked drop shadows is texture, not
 * hierarchy; a hairline frame with a left rail separates one row from the next and then gets out of the way.
 */

import { nonBlankErrorMessage } from '@federa/utils/errors/error-core';
import { VARIANT_OUTLINED } from '@federa/const/variant-values';
import { NoticeAlert } from './NoticeAlert.tsx';
import { Card } from '../primitives/Card.tsx';
import { SkeletonLine } from '../primitives/Skeleton.tsx';
import { VStack } from '../layout/Stack.tsx';
import { ARIA_ROLES } from '../common/aria-tokens.ts';
import { ROW_RAIL_STYLE } from '../common/service-surface-styles.ts';
import { HAIRLINE_LIST_STYLE, HAIRLINE_ROW_TEXT_STYLE, hairlineRowStyle } from '../common/hairline-list-styles.ts';
import { FailedRead } from '../views/self-service-parts.tsx';
import { spacing } from '../css-refs.spacing.ts';
import type { ReadRetry } from '@federa/types/ui-read-retry';

/** Three placeholder rows: enough to establish the rhythm the real list lands in, not enough to promise a length. */
const SKELETON_KEYS = ['first', 'second', 'third'] as const;

/** Staggered placeholder widths, so the block reads as a row of text rather than as three identical bars. ONE set for
 *  every surface: the two that drew this each picked their own numbers for the same three-line silhouette (a title, a
 *  body line, a metadata line), which is a difference nobody chose and nobody could see. */
export const SKELETON_LINE_WIDTHS = { title: '46%', body: '88%', meta: '28%' } as const;

/** A failed query as ONE operator-visible line, or null while `isError` is false. Takes the WHOLE query object on
 *  purpose (see the module docstring), and extracts through the shared `nonBlankErrorMessage`, whose string and
 *  message-bearing-object arms are exactly what the hand-rolled `instanceof Error` spellings at the adopting sites
 *  discarded -- those showed the generic fallback where a real cause was available. */
export function readQueryError(query: { isError: boolean; error: unknown }, fallback: string): string | null {
	return query.isError ? nonBlankErrorMessage(query.error, fallback) : null;
}

/**
 * A read that failed: the notice every surface draws (`NoticeAlert`: authored headline and recovery step, the
 * transport's words last and quietest) inside the kit's failed read (`FailedRead`), with the read's way back (`retry`)
 * when the surface can read again.
 *
 * THE TRANSPORT'S OWN WORDS SIT IN THE ALERT, demoted to a caption and heard with it -- a refusal's own sentence rides
 * there -- UNLESS A RETRY CAN RUN: the read clears them while it runs, and in the alert their going moved the Try again
 * under it from beneath the pointer (critics, 2026-09-27), so there they are the failed read's own last line.
 */
export function FetchErrorAlert(props: { title: string; body: string; detail: string | null; retry?: ReadRetry }) {
	const underButton = props.retry !== undefined;
	return (
		<FailedRead retry={props.retry} detail={underButton ? props.detail : null}>
			<NoticeAlert
				notice={{ variant: 'error', title: props.title, body: props.body, subject: null, detail: underButton ? null : props.detail }}
			/>
		</FailedRead>
	);
}

/** Placeholder for a list drawn as ONE box of hairline rows (`hairline-list-styles`): the same box and the same rows,
 *  so the page keeps its shape when the data lands rather than swapping a stack of cards for one box. */
export function HairlineListSkeleton({ label }: { label: string }) {
	return (
		<div style={HAIRLINE_LIST_STYLE} role={ARIA_ROLES.status} aria-label={label}>
			{SKELETON_KEYS.map((key, index) => (
				<div key={key} role={ARIA_ROLES.presentation} style={hairlineRowStyle(index === 0)}>
					<VStack gap={spacing[2]} style={HAIRLINE_ROW_TEXT_STYLE}>
						<SkeletonLine width={SKELETON_LINE_WIDTHS.title} />
						<SkeletonLine width={SKELETON_LINE_WIDTHS.body} />
						<SkeletonLine width={SKELETON_LINE_WIDTHS.meta} />
					</VStack>
				</div>
			))}
		</div>
	);
}

/** Placeholder in the real row's silhouette, so a list does not jump when its data lands. */
export function ListSkeleton({ label }: { label: string }) {
	return (
		<VStack gap={spacing.contextual.relatedItems} role={ARIA_ROLES.status} aria-label={label}>
			{SKELETON_KEYS.map((key) => (
				<Card key={key} variant={VARIANT_OUTLINED} padding="md" role={ARIA_ROLES.presentation} style={ROW_RAIL_STYLE}>
					<VStack gap={spacing[2]}>
						<SkeletonLine width={SKELETON_LINE_WIDTHS.title} />
						<SkeletonLine width={SKELETON_LINE_WIDTHS.body} />
						<SkeletonLine width={SKELETON_LINE_WIDTHS.meta} />
					</VStack>
				</Card>
			))}
		</VStack>
	);
}
